/* Demo Control Center — SUPER ADMIN ONLY presentation tooling.
   Operates exclusively on the isolated, flagged demo dataset. */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  MonitorPlay, RotateCcw, Rocket, Bell, Database, Store, Gauge, BarChart3, Star,
  Loader2, CheckCircle2, ShieldAlert, Play,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const SCENARIOS = [
  ["rear_end", "Rear-End Collision"], ["commercial_truck", "Commercial Trucking Accident"],
  ["drunk_driver", "DUI Crash"], ["motorcycle", "Motorcycle Accident"],
  ["pedestrian", "Pedestrian Struck"], ["multi_vehicle", "Multi-Vehicle Pileup"],
  ["hit_and_run", "Hit-and-Run"], ["uninsured_driver", "Uninsured Driver"],
];
const NOTIFY = [
  ["new_accident", "New Accident"], ["rn_needed", "RN Needed"], ["drone_requested", "Drone Requested"],
  ["attorney_assigned", "Attorney Assigned"], ["retainer_signed", "Retainer Signed"],
  ["crm_sync_complete", "CRM Sync Complete"], ["telehealth_scheduled", "Telehealth Scheduled"],
  ["new_marketplace_case", "New Marketplace Case"],
];
const SEGMENTS = [
  ["clients", "Clients"], ["attorneys", "Attorneys"], ["nurses", "Nurses (8 RNs)"],
  ["telehealth", "Telehealth Providers (5)"], ["pilots", "Drone Pilots (5)"],
  ["affiliates", "Affiliates (10)"], ["admins", "Admin Users"], ["marketplace", "Marketplace Cases"],
];
const SPEEDS = [["slow", "Slow"], ["normal", "Normal"], ["fast", "Fast"], ["presentation", "Presentation Mode"]];

const Card = ({ icon: Icon, title, sub, children, tid }) => (
  <div className="bg-white rounded-3xl border border-slate-100 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={tid}>
    <div className="flex items-center gap-2">
      <Icon className="w-5 h-5 text-cobalt" />
      <h3 className="font-heading font-bold text-navy">{title}</h3>
    </div>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    <div className="mt-4">{children}</div>
  </div>
);

const Chip = ({ onClick, busy, children, tid }) => (
  <button onClick={onClick} disabled={busy} data-testid={tid}
    className="px-3.5 py-2 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-cobalt hover:text-white transition-all disabled:opacity-50">
    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : children}
  </button>
);

export default function DemoControlCenter() {
  const { user } = useAuth();
  const [busy, setBusy] = useState("");
  const [speed, setSpeed] = useState("presentation");
  const [life, setLife] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const pollRef = useRef(null);

  const loadAnalytics = () => api.get("/admin/demo-control/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
  const loadLifecycle = () => api.get("/admin/demo-control/lifecycle-status").then(({ data }) => {
    setLife(data);
    if (!data.running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; loadAnalytics(); }
  }).catch(() => {});
  useEffect(() => { loadAnalytics(); loadLifecycle(); return () => pollRef.current && clearInterval(pollRef.current); }, []);

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try { const r = await fn(); toast.success(okMsg(r?.data)); loadAnalytics(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };

  const startLifecycle = () => act("lifecycle",
    async () => {
      const r = await api.post("/admin/demo-control/lifecycle", { speed });
      pollRef.current = setInterval(loadLifecycle, 2000);
      return r;
    }, () => `Full accident lifecycle started (${speed} speed)`);

  if (!user?.super_admin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl border border-slate-100 p-8 text-center max-w-md" data-testid="demo-control-denied">
          <ShieldAlert className="w-10 h-10 text-amber-500 mx-auto" />
          <h2 className="font-heading font-bold text-navy mt-3">Super Admin Only</h2>
          <p className="text-sm text-slate-500 mt-1">The Demo Control Center is an internal presentation tool restricted to the Super Admin role.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 sm:px-6 py-8" data-testid="demo-control-center">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center gap-3">
          <MonitorPlay className="w-7 h-7 text-cobalt" />
          <h1 className="font-heading text-2xl font-bold text-navy">Demo Control Center</h1>
          <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wide" data-testid="demo-data-badge">Demo Data · Internal Only</span>
        </div>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">Investor meetings, attorney demos, conferences and live sales presentations. Every action operates exclusively on the isolated demo dataset — production is never touched.</p>

        <div className="mt-6 grid lg:grid-cols-2 gap-5">
          <Card icon={RotateCcw} title="Reset Demo Environment" sub="One click restores months of realistic activity — identical, polished state for every presentation." tid="dcc-reset-card">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="dcc-reset-btn" disabled={busy === "reset"}
                  className="px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors disabled:opacity-50">
                  {busy === "reset" ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null} Reset Demo
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="dcc-reset-confirm">
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset Demo Environment?</AlertDialogTitle>
                  <AlertDialogDescription>This will restore a clean presentation environment — all demo activity is regenerated with realistic historical timestamps. Production data is not affected.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="dcc-reset-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="dcc-reset-do" onClick={() =>
                    act("reset", () => api.post("/admin/demo-control/reset"),
                        (d) => `Demo reset — ${d?.seeded?.cases || 0} cases restored`)}>
                    Reset Demo
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {analytics?.last_reset && <p className="text-[11px] text-slate-400 mt-2" data-testid="dcc-last-reset">Last reset: {new Date(analytics.last_reset).toLocaleString()}</p>}
          </Card>

          <Card icon={Star} title="Michael Jackson — Featured Demo" sub="The complete 'perfect' workflow: intake → RN → telehealth → attorney → retainer → CRM sync → settlement progress." tid="dcc-featured-card">
            <div className="flex flex-wrap gap-1.5">
              {["Intake", "RN Review", "Telehealth", "Attorney Assigned", "Retainer Signed", "CRM Synced", "Timeline", "Settlement Progress"].map((s) => (
                <span key={s} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
                  <CheckCircle2 className="w-3 h-3" /> {s}
                </span>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Restored automatically on every reset — use this case for live walk-throughs.</p>
          </Card>

          <Card icon={Rocket} title="Demo Scenario Launcher" sub="Instantly generates a complete case: client, photos, police report, injuries, insurance, RN queue, notifications and marketplace listing." tid="dcc-scenarios-card">
            <div className="flex flex-wrap gap-2">
              {SCENARIOS.map(([k, label]) => (
                <Chip key={k} tid={`dcc-scenario-${k}`} busy={busy === `sc-${k}`}
                  onClick={() => act(`sc-${k}`, () => api.post("/admin/demo-control/scenario", { type: k }),
                                     (d) => `${label} launched — ${d?.client_name} in ${d?.city}, ${d?.state}`)}>
                  {label}
                </Chip>
              ))}
            </div>
          </Card>

          <Card icon={Bell} title="Demo Notifications" sub="Production-identical push notifications on demand." tid="dcc-notify-card">
            <div className="flex flex-wrap gap-2">
              {NOTIFY.map(([k, label]) => (
                <Chip key={k} tid={`dcc-notify-${k}`} busy={busy === `nt-${k}`}
                  onClick={() => act(`nt-${k}`, () => api.post("/admin/demo-control/notify", { kind: k }),
                                     () => `${label} notification sent`)}>
                  {label}
                </Chip>
              ))}
            </div>
          </Card>

          <Card icon={Play} title="Live Workflow Simulator" sub="Run Full Accident Lifecycle — every step executes real application workflows with realistic delays." tid="dcc-lifecycle-card">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Gauge className="w-4 h-4 text-slate-400" />
              {SPEEDS.map(([k, label]) => (
                <button key={k} onClick={() => setSpeed(k)} data-testid={`dcc-speed-${k}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${speed === k ? "bg-cobalt text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{label}</button>
              ))}
            </div>
            <button onClick={startLifecycle} disabled={life?.running || busy === "lifecycle"} data-testid="dcc-run-lifecycle"
              className="px-5 py-2.5 rounded-full bg-cobalt hover:bg-cobalt-hover text-white text-sm font-semibold transition-colors disabled:opacity-50">
              {life?.running ? <Loader2 className="w-4 h-4 animate-spin inline mr-1.5" /> : <Play className="w-4 h-4 inline mr-1.5" />}
              {life?.running ? "Lifecycle running…" : "Run Full Accident Lifecycle"}
            </button>
            {life?.steps?.length > 0 && (
              <div className="mt-3 space-y-1.5 max-h-56 overflow-auto" data-testid="dcc-lifecycle-steps">
                {life.steps.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> {s.label}
                    <span className="ml-auto text-[10px] text-slate-400">{new Date(s.at).toLocaleTimeString()}</span>
                  </div>
                ))}
                {life.running && <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> next step…</div>}
              </div>
            )}
          </Card>

          <Card icon={Database} title="Seed Data Manager" sub="Reseed any population segment on demand." tid="dcc-seed-card">
            <div className="flex flex-wrap gap-2">
              {SEGMENTS.map(([k, label]) => (
                <Chip key={k} tid={`dcc-seed-${k}`} busy={busy === `sd-${k}`}
                  onClick={() => act(`sd-${k}`, () => api.post("/admin/demo-control/seed", { segment: k }),
                                     () => `${label} seeded`)}>
                  {label}
                </Chip>
              ))}
            </div>
          </Card>

          <Card icon={Store} title="Marketplace Seeder" sub="Tops the marketplace up to ~20 active cases — varied severities, carriers, accident types, values and cities. No duplicates." tid="dcc-marketplace-card">
            <button data-testid="dcc-marketplace-topup" disabled={busy === "mkt"}
              onClick={() => act("mkt", () => api.post("/admin/demo-control/marketplace-topup"),
                                 (d) => `Marketplace: ${d?.active_after} active cases (${d?.created} added)`)}
              className="px-5 py-2.5 rounded-full bg-navy hover:opacity-90 text-white text-sm font-semibold transition-opacity disabled:opacity-50">
              {busy === "mkt" ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null} Top Up Marketplace
            </button>
          </Card>

          <Card icon={BarChart3} title="Presentation Analytics" sub="Demos run, most-used workflows, reset history." tid="dcc-analytics-card">
            {analytics ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                  {[["Demos Run", analytics.demos_run], ["Lifecycle Runs", analytics.lifecycle_runs],
                    ["Resets", analytics.resets],
                    ["Top Scenario", analytics.most_launched_scenario ? analytics.most_launched_scenario.replace(/_/g, " ") : "—"]].map(([l, v]) => (
                    <div key={l} className="rounded-xl bg-slate-50 py-2.5">
                      <div className="font-heading font-extrabold text-sm text-navy capitalize">{v}</div>
                      <div className="text-[9px] uppercase text-slate-400">{l}</div>
                    </div>
                  ))}
                </div>
                {analytics.recent_events?.length > 0 && (
                  <div className="mt-3 space-y-1 max-h-40 overflow-auto" data-testid="dcc-analytics-events">
                    {analytics.recent_events.slice(0, 10).map((e) => (
                      <div key={e.event_id} className="flex items-center gap-2 text-[11px] text-slate-500">
                        <span className="px-1.5 py-0.5 rounded bg-slate-100 font-semibold uppercase text-[9px]">{e.kind}</span>
                        {e.detail?.scenario || e.detail?.segment || e.detail?.kind || e.detail?.speed || ""}
                        <span className="ml-auto text-slate-400">{new Date(e.at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : <p className="text-xs text-slate-400">No presentation activity yet.</p>}
          </Card>
        </div>
      </div>
    </div>
  );
}
